import {
  AlertCircle,
  AlertTriangle,
  ArrowRight,
  Check,
  Layers,
  Package,
} from "lucide-react"
import type { MaterialsAutofillAnalysisContext } from "../../../../services/materialsQuotationAutofillDraftService.ts"
import styles from "./MaterialsAutofillAnalysisCard.module.scss"

export type AutofillImportSummary = {
  materialCount: number
  categoryCount: number
  fromCatalogCount: number
  customOrUnmatchedCount: number
}

type MaterialsAutofillAnalysisCardProps = {
  analysisContext: MaterialsAutofillAnalysisContext | null
  autofillImportSummary: AutofillImportSummary
  isEstimacionDeCostos?: boolean
  estimationCatalogGapCount?: number
  onRequestQuotesFromEstimation?: () => void
}

const relevanceBannerText = (ctx: MaterialsAutofillAnalysisContext): string | null => {
  const dr = ctx.documentRelevance
  if (dr === "unlikely_materials_list") {
    return "El archivo no encaja del todo con un listado típico de materiales; revisá el resumen con detención."
  }
  if (dr === "other_or_unclear") {
    return "No quedó claro si el documento es un listado o presupuesto de materiales; conviene validar cada material en el resumen."
  }
  return null
}

const formatCount = (count: number) => new Intl.NumberFormat("es-AR").format(count)

const MaterialsAutofillAnalysisCard = ({
  analysisContext,
  autofillImportSummary,
  isEstimacionDeCostos = false,
  estimationCatalogGapCount = 0,
  onRequestQuotesFromEstimation,
}: MaterialsAutofillAnalysisCardProps) => {
  const relevanceText = analysisContext ? relevanceBannerText(analysisContext) : null
  const summary = analysisContext?.summary?.trim()
  const notesForUser = analysisContext?.notesForUser?.trim()
  const { categoryCount, fromCatalogCount, customOrUnmatchedCount } = autofillImportSummary

  const instructionText = isEstimacionDeCostos
    ? "Revisá los materiales, ajustá cantidades y descargá tu estimación."
    : "Revisá los materiales, ajustá cantidades y publicá tu lista para recibir presupuestos."

  const gapCount = isEstimacionDeCostos ? estimationCatalogGapCount : customOrUnmatchedCount

  const inlineWarningLabel =
    gapCount === 1
      ? isEstimacionDeCostos
        ? "sin precio"
        : "sin reconocer en catálogo"
      : isEstimacionDeCostos
        ? "sin precio"
        : "sin reconocer en catálogo"

  const secondaryTitle = isEstimacionDeCostos
    ? gapCount === 1
      ? "1 material sin precio de referencia en el catálogo"
      : `${formatCount(gapCount)} materiales sin precio de referencia en el catálogo`
    : gapCount === 1
      ? "1 material no reconocido en catálogo"
      : `${formatCount(gapCount)} materiales no reconocidos en catálogo`

  const secondaryBody = isEstimacionDeCostos
    ? "Podés pedir cotización a proveedores de Edify para conseguir precios reales para estos ítems."
    : "Revisá el resumen a la derecha: podés editarlos, enlazarlos al catálogo o agregar materiales manualmente."

  const showSecondaryCard = gapCount > 0 && (isEstimacionDeCostos ? Boolean(onRequestQuotesFromEstimation) : true)

  return (
    <div className={styles.stack}>
      {relevanceText ? (
        <div
          className={`${styles.relevanceBanner} ${
            analysisContext?.documentRelevance === "unlikely_materials_list"
              ? styles.relevanceBanner_warn
              : styles.relevanceBanner_muted
          }`}
          role="status"
        >
          {relevanceText}
        </div>
      ) : null}

      <div className={styles.primaryCard} role="region" aria-label="Resultado del análisis del archivo">
        <div className={styles.header}>
          <div className={styles.iconWrap} aria-hidden>
            <Check size={22} strokeWidth={2.5} />
          </div>
          <div className={styles.headerContent}>
            <h3 className={styles.title}>Lista de materiales detectada</h3>
            {summary ? <p className={styles.summary}>{summary}</p> : null}
            <p className={styles.instruction}>{instructionText}</p>
            {notesForUser ? <p className={styles.notes}>{notesForUser}</p> : null}
          </div>
        </div>

        <div className={styles.stats}>
          {fromCatalogCount > 0 ? (
            <div className={styles.stat}>
              <Package size={18} className={styles.statIcon} aria-hidden />
              <span>
                <span className={styles.statValue}>{formatCount(fromCatalogCount)}</span>{" "}
                {fromCatalogCount === 1
                  ? "material enlazado al catálogo"
                  : "materiales enlazados al catálogo"}
              </span>
            </div>
          ) : null}
          <div className={styles.stat}>
            <Layers size={18} className={styles.statIcon} aria-hidden />
            <span>
              <span className={styles.statValue}>{formatCount(categoryCount)}</span>{" "}
              {categoryCount === 1 ? "categoría detectada" : "categorías detectadas"}
            </span>
          </div>
        </div>

        {gapCount > 0 ? (
          <div className={styles.inlineWarning} role="status">
            <AlertTriangle size={18} className={styles.inlineWarningIcon} aria-hidden />
            <span className={styles.inlineWarningText}>
              <strong>
                {formatCount(gapCount)} {inlineWarningLabel}
              </strong>
              {isEstimacionDeCostos ? " — revisalos" : " — revisalos en el resumen"}
            </span>
          </div>
        ) : null}
      </div>

      {showSecondaryCard ? (
        <div className={styles.secondaryCard} role="region" aria-label="Materiales que requieren atención">
          <div className={styles.secondaryHeader}>
            <div className={styles.secondaryIconWrap} aria-hidden>
              <AlertCircle size={18} />
            </div>
            <h4 className={styles.secondaryTitle}>{secondaryTitle}</h4>
          </div>
          <p className={styles.secondaryText}>{secondaryBody}</p>
          {isEstimacionDeCostos && onRequestQuotesFromEstimation ? (
            <button type="button" className={styles.secondaryAction} onClick={onRequestQuotesFromEstimation}>
              <ArrowRight size={16} aria-hidden />
              Ir a pedir cotización con esta lista
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

export default MaterialsAutofillAnalysisCard
